import { Skeleton, SkeletonAvatar } from '@parsina/framework';
import { C, RADIUS, SHADOW } from '@parsina/framework/tokens';
import { STACK } from '../constants/density';

/**
 * The placeholder for one `MobileCard`.
 *
 * It is built from the kit's Skeleton atoms rather than from `SkeletonCard`
 * because the kit card is a desktop `Card` — 20px padding, a 48px avatar, a
 * divider and two action slots — and a record card on this phone surface is
 * none of those. The geometry here is `MobileCard`'s own (padding, radius,
 * border, shadow) and the rows are the ones a card actually draws: a head
 * line (title + trailing badge), `metaLines` meta lines, and an optional
 * action shelf at `MobileCardActions`' size. So when the data lands, the card
 * that replaces this one occupies the same box, and the list does not jump
 * (DESIGN.md §"Loading & skeleton" — geometry).
 *
 * Each list tunes it to its own card through `MobileList`'s `renderSkeleton`:
 * an appointment card has a 40px avatar and an action shelf, an item card has
 * neither.
 */
export default function MobileCardSkeleton({
  /** A leading disc, at the size the card's `Avatar` renders (0 for none). */
  avatar = 0,
  /** Meta lines under the head line. */
  metaLines = 1,
  /** A trailing pill on the head line (a status badge), 0 for none. */
  badge = 56,
  /** Buttons in an action shelf, 0 for none. */
  actions = 0,
  /** Small pills under the meta (tags, chips). */
  chips = 0,
  style,
  ...rest
}) {
  return (
    <div
      aria-hidden
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: RADIUS.card,
        boxShadow: SHADOW.card,
        padding: '0.75rem 0.875rem',
        minHeight: '2.75rem',
        ...style,
      }}
      {...rest}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        {avatar > 0 ? <SkeletonAvatar size={avatar} /> : null}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Skeleton width="58%" height={15} />
          {Array.from({ length: metaLines }, (_, i) => (
            <Skeleton key={i} width={`${34 + ((i * 13) % 30)}%`} height={11} />
          ))}
        </div>
        {badge > 0 ? <Skeleton width={badge} height={22} radius={RADIUS.pill} /> : null}
      </div>

      {chips > 0 ? (
        <div style={{ display: 'flex', gap: 6, marginTop: '0.5rem' }}>
          {Array.from({ length: chips }, (_, i) => (
            <Skeleton key={i} width={52 + (i % 2) * 16} height={20} radius={RADIUS.pill} />
          ))}
        </div>
      ) : null}

      {actions > 0 ? (
        <div style={{
          display: 'flex', gap: `${STACK.tight}px`, alignItems: 'center',
          marginTop: '0.625rem', paddingTop: '0.625rem',
          borderTop: `1px solid ${C.border}`,
        }}>
          {Array.from({ length: actions }, (_, i) => (
            <Skeleton key={i} height={36} radius={RADIUS.control} style={{ flex: i === 0 ? 1 : 0, width: i === 0 ? 'auto' : 36 }} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
